import { DIFFICULTY_TIERS, shortenId, type Difficulty } from '@dohhh/engine';
import type { ConnectionStatus } from '@dohhh/net';
import { Chip, Typography } from '@heroui/react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Page frame. Every screen is a single column that fits a phone in one hand. */
export function Screen({
  title,
  subtitle,
  children,
  aside,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  aside?: ReactNode;
}): ReactNode {
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-5 px-5 pb-44 pt-[max(1.25rem,env(safe-area-inset-top))]">
      {(title !== undefined || aside !== undefined) && (
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title !== undefined && (
              <Typography.Heading level={1} className="text-2xl font-semibold tracking-tight">
                {title}
              </Typography.Heading>
            )}
            {subtitle !== undefined && (
              <Typography.Paragraph className="mt-1 text-sm text-muted">
                {subtitle}
              </Typography.Paragraph>
            )}
          </div>
          {aside}
        </header>
      )}
      {children}
    </main>
  );
}

/** Sticky bottom bar, because the primary action must be reachable by a thumb. */
export function ActionBar({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border/40 bg-background/80 px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
      <div className="mx-auto flex w-full max-w-md flex-col gap-2">{children}</div>
    </div>
  );
}

export function ConnectionPill({
  status,
  peerCount,
  everConnected = false,
}: {
  status: ConnectionStatus;
  peerCount: number;
  /** Distinguishes "still waiting for the first peer" from "someone just dropped." */
  everConnected?: boolean;
}): ReactNode {
  const { t } = useTranslation('common');
  const { colour, label } = describe(t, status, peerCount, everConnected);
  return (
    <Chip color={colour} variant="soft" size="sm" className="shrink-0">
      {label}
    </Chip>
  );
}

function describe(
  t: (key: string, options?: Record<string, unknown>) => string,
  status: ConnectionStatus,
  peerCount: number,
  everConnected: boolean,
): { colour: 'success' | 'warning' | 'danger' | 'default'; label: string } {
  switch (status) {
    case 'connected':
      // peerCount is everyone ELSE on the mesh; the pill should read as "how
      // many devices are in this game", which includes the one showing it.
      return { colour: 'success', label: t('connection.devices_count', { count: peerCount + 1 }) };
    case 'connecting':
      return {
        colour: 'warning',
        label: everConnected ? t('connection.reconnecting') : t('connection.connecting'),
      };
    case 'alone':
      // Same underlying status either way, but a very different thing to
      // tell a player: a host who has never had a joiner is just waiting;
      // someone who *had* a peer and lost them needs to know that dropped,
      // not that nobody ever showed up.
      return {
        colour: 'warning',
        label: everConnected ? t('connection.reconnecting') : t('connection.waiting_for_others'),
      };
    case 'failed':
      // Said plainly rather than spun forever: with no server there is no relay
      // of last resort, and some networks simply will not carry this (R-15).
      return { colour: 'danger', label: t('connection.no_connection') };
    default:
      return { colour: 'default', label: status };
  }
}

const TIER_CLASS: Record<Difficulty, string> = {
  bscba: 'border-tier-bscba/60 text-tier-bscba',
  msc: 'border-tier-msc/60 text-tier-msc',
  phd: 'border-tier-phd/60 text-tier-phd',
  professor: 'border-tier-professor/60 text-tier-professor',
};

/** The bet, stated as a bet: what you win, what it costs. */
export function TierBadge({ difficulty }: { difficulty: Difficulty }): ReactNode {
  const { t } = useTranslation('common');
  const tier = DIFFICULTY_TIERS[difficulty];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${TIER_CLASS[difficulty]}`}
    >
      {t(`tiers.${difficulty}`)}
      <span className="tabular-nums opacity-80">
        +{tier.award} / {tier.penalty}
      </span>
    </span>
  );
}

/** A player id, shortened. Present but never prominent (R-17). */
export function PlayerTag({ id, username }: { id: string; username: string }): ReactNode {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="truncate font-medium">{username}</span>
      <span className="font-mono text-[0.65rem] text-muted">{shortenId(id)}</span>
    </span>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  children: ReactNode;
}): ReactNode {
  const classes = {
    info: 'border-default/40 bg-default/10 text-default-foreground',
    warn: 'border-warning/40 bg-warning/10 text-warning',
    danger: 'border-danger/40 bg-danger/10 text-danger-text',
  }[tone];
  // A notice that appears after the fact (a refused join, a stalled lobby)
  // is silent to a screen reader unless something marks it as a status
  // message (WCAG 4.1.3) - danger is assertive because it interrupts what
  // the player was doing, the rest can wait for a pause in speech.
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      className={`rounded-xl border px-4 py-3 text-sm ${classes}`}
    >
      {children}
    </div>
  );
}

/**
 * "Connecting" that never resolves is the failure the review insisted must not
 * happen silently (R-15): with no server there is no relay of last resort, and
 * some networks - carrier NAT especially - simply will not carry a direct peer
 * connection. After a while of nobody arriving, say so and say what to do.
 */
export function StalledWarning({
  status,
  peerCount,
  everConnected = false,
  afterMs = 120_000,
}: {
  status: ConnectionStatus;
  peerCount: number;
  everConnected?: boolean;
  afterMs?: number;
}): ReactNode {
  const { t } = useTranslation('common');
  const stalled = useElapsed(afterMs) && peerCount === 0 && status !== 'failed';

  if (status === 'failed') {
    return <Notice tone="danger">{t('stalled.relay_failed')}</Notice>;
  }
  if (!stalled) return null;
  if (everConnected) {
    return <Notice tone="warn">{t('stalled.lost_connection')}</Notice>;
  }
  return <Notice tone="warn">{t('stalled.nobody_here')}</Notice>;
}

/** True once `ms` has passed since this hook first mounted with this duration. */
export function useElapsed(ms: number): boolean {
  const [passed, setPassed] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setPassed(true), ms);
    return () => clearTimeout(id);
  }, [ms]);
  return passed;
}

/**
 * Matches the content bank's `Base_sub` / `Base_{sub}` formula notation
 * (e.g. `k_eff`, `Δf_{rep}`) - most subscripts in the bank are already
 * pre-converted to real Unicode subscript characters at authoring time
 * (see the content build's subscript pass), which covers every letter that
 * has one. The letters that don't (b, c, d, f, g, q, w, y, z) are left in
 * this literal underscore form on purpose, because there is no character to
 * convert them to - {@link FormulaText} renders those the rest of the way
 * with a real `<sub>` element instead, which CSS can shrink and drop for
 * any letter.
 */
const FORMULA_SUBSCRIPT_RE = /([A-Za-zΔΩθτλνμΦΨΣαβγ]+)_\{?([A-Za-z0-9]+)\}?/g;

/** Renders question/option/explanation text, turning any leftover `Base_sub` notation into a real subscript. */
export function FormulaText({ text }: { text: string }): ReactNode {
  FORMULA_SUBSCRIPT_RE.lastIndex = 0;
  if (!FORMULA_SUBSCRIPT_RE.test(text)) return text;
  FORMULA_SUBSCRIPT_RE.lastIndex = 0;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = FORMULA_SUBSCRIPT_RE.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <span key={key++}>
        {match[1]}
        <sub>{match[2]}</sub>
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
